import {ChangeDetectorRef, Component, Inject, OnInit} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogModule, MatDialogRef} from "@angular/material/dialog";
import {MatFormFieldModule} from "@angular/material/form-field";
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators
} from "@angular/forms";
import {MatInputModule} from "@angular/material/input";
import {MatButtonModule} from "@angular/material/button";
import {
  MatAutocompleteModule,
  MatAutocompleteSelectedEvent,
  MatAutocompleteTrigger
} from "@angular/material/autocomplete";
import {COMMA, ENTER} from "@angular/cdk/keycodes";
import {MatChipInput, MatChipInputEvent, MatChipsModule} from "@angular/material/chips";
import {MatIconModule} from "@angular/material/icon";
import {map, Observable, startWith} from "rxjs";
import {AsyncPipe} from "@angular/common";
import {MatOptionModule} from "@angular/material/core";
import {MatTableDataSource, MatTableModule} from "@angular/material/table";
import {MatSlideToggleModule} from "@angular/material/slide-toggle";
import {MatButtonToggle, MatButtonToggleChange, MatButtonToggleGroup} from "@angular/material/button-toggle";

export interface RoleGroupModuleOption {
  moduleGroupName: string,
  principleRoles: { label: string, code: string }[]
}

export interface NewGrantedUser {
  name: string;
  surname: string;
  email: string;
}

@Component({
  selector: 'app-add-grants-dialog',
  standalone: true,
  imports: [
    MatFormFieldModule,
    MatInputModule,
    FormsModule,
    MatButtonModule,
    MatDialogModule,
    ReactiveFormsModule,
    MatAutocompleteTrigger,
    MatIconModule,
    MatChipsModule,
    MatChipInput,
    MatAutocompleteModule,
    MatOptionModule,
    AsyncPipe,
    MatTableModule,
    MatSlideToggleModule,
    MatButtonToggleGroup,
    MatButtonToggle,
  ],
  templateUrl: './add-grants-dialog.component.html',
  styleUrl: './add-grants-dialog.component.scss'
})
export class AddGrantsDialogComponent implements OnInit {

  grantUsersForm: FormGroup = this.fb.group({
    users: this.fb.array([
      this.buildUserForm()
    ]),
    roleCtrl: [Validators.required]
  });

  formMode: 'single' | 'multiple' | 'self' = 'single';
  selectedRoles: { label: string; code: string }[] = [];
  filteredRoleOptions!: Observable<RoleGroupModuleOption[]>;
  allRoles: { label: string; code: string }[] = [];
  datasource = new MatTableDataSource();

  readonly separatorKeysCodes: number[] = [ENTER, COMMA];

  constructor(
    public dialogRef: MatDialogRef<AddGrantsDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { roleGroupOptions: RoleGroupModuleOption[], currentUser: NewGrantedUser },
    private fb: FormBuilder,
    private cdr: ChangeDetectorRef
  ) {
    this.allRoles = this.data.roleGroupOptions.flatMap(group => group.principleRoles);
  }

  get userForm(): FormGroup {
    return (this.grantUsersForm.get('users') as FormArray).at(0) as FormGroup;
  }

  get users(): FormArray {
    return this.grantUsersForm.get('users') as FormArray;
  }

  get roleCtrl(): FormControl {
    return this.grantUsersForm.get('roleCtrl') as FormControl;
  }

  ngOnInit(): void {
    this.datasource.data = this.users.controls;

    this.filteredRoleOptions = this.roleCtrl.valueChanges.pipe(
      startWith(null),
      map((role: string | null) => role ? this._filter(role) : this.data.roleGroupOptions.slice())
    );
  }

  private buildUserForm(user?: NewGrantedUser): FormGroup {
    return this.fb.group({
      name: [user?.name ?? '', [Validators.required, Validators.pattern(/^[a-zA-Z]+$/)]],
      surname: [user?.surname ?? '', [Validators.required, Validators.pattern(/^[a-zA-Z]+$/)]],
      email: [user?.email ?? '', [Validators.required, Validators.email]]
    });
  }

  addUser(user?: any): void {
    this.users.push(this.buildUserForm(user));
    this.datasource.data = [...this.users.controls];
    this.cdr.detectChanges();
  }

  removeUser(index: number): void {
    this.users.removeAt(index);
    this.datasource.data = [...this.users.controls];
  }

  removeAllUsers(): void {
    this.users.clear();
    this.datasource.data = [...this.users.controls];
  }

  add(event: MatChipInputEvent): void {
    const input = event.chipInput.inputElement;
    const value = event.value;

    if ((value || '').trim()) {
      const selectedRole = this.allRoles.find(role => role.label.toLowerCase() === value.trim().toLowerCase());

      if (selectedRole && !this.selectedRoles.some(role => role.code === selectedRole.code)) {
        this.selectedRoles.push(selectedRole);
      }
    }

    if (input) {
      input.value = '';
    }
    this.roleCtrl.setValue(null);
  }

  selected(event: MatAutocompleteSelectedEvent): void {
    this.selectedRoles.push(event.option.value);
    this.roleCtrl.setValue(null);
  }

  remove(role: { label: string; code: string }): void {
    const index = this.selectedRoles.indexOf(role);

    if (index >= 0) {
      this.selectedRoles.splice(index, 1);
    }
  }

  onSubmit(): void {
    if (this.selectedRoles.length === 0 || !this.roleCtrl.valid || !this.users.valid) return;

    let users: NewGrantedUser[] = [];
    if (this.users.valid) {
      users = this.users.controls.map(control => control.value);
    }

    if (users.length > 0) {
      this.dialogRef.close({
        users,
        roleCodes: this.selectedRoles.map(role => role.code)
      });
    }
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  private _filter(value: string): RoleGroupModuleOption[] {
    const filterValue = value.toLowerCase();

    return this.data.roleGroupOptions.map(group => ({
      moduleGroupName: group.moduleGroupName,
      principleRoles: group.principleRoles.filter(role => role.label.toLowerCase().includes(filterValue))
    })).filter(group => group.principleRoles.length > 0);
  }

  onChangeUsersFormMode($event: MatButtonToggleChange) {
    if (this.formMode === 'multiple') return;

    const defaultValue = this.transferFormValue();
    this.removeAllUsers();
    this.addUser(defaultValue);
  }

  private currentUser(): NewGrantedUser {
    const user = this.data.currentUser ?? {};
    return {
      name: user.name,
      surname: user.surname,
      email: user.email
    } as NewGrantedUser;
  }

  private transferFormValue(): any {
    switch (this.formMode) {
      case 'self':
        return this.currentUser();
      case 'single':
        return this.users.length > 0 ? this.users.value[0] : this.buildUserForm().value;
    }
    return null;
  }
}
